import { ReportDetailsQuery, useUpdateReportMutation } from '@/__generated__/graphql';
import { GenericModal } from '@/components/shared/GenericModal';
import { removeChaseItemsDialog } from './styles';

import { Alert, Button, DialogActions, DialogContent, Typography } from '@mui/material';
import { Dispatch, SetStateAction, useCallback } from 'react';
import { abyssColors } from '@/theme/colors';
import { SnackbarMessage } from '@/types';

export const RemoveChaseItemsDialog = ({
  open,
  onClose,
  setSelectedReport,
  selectedChaseItems,
  selectedReport,
  setSnackbarMessage,
}: {
  open: boolean;
  onClose: () => void;
  selectedChaseItems: string[];
  setSelectedReport: Dispatch<SetStateAction<ReportDetailsQuery['report'] | undefined>>;
  selectedReport: ReportDetailsQuery['report'] | undefined;
  setSnackbarMessage: Dispatch<SetStateAction<SnackbarMessage>>;
}) => {
  const [updateListOfChaseItems, { loading }] = useUpdateReportMutation();

  const handleClose = () => onClose();

  const onUpdateListOfChaseItems = useCallback(() => {
    if (!selectedReport || !selectedReport.id) return;

    const updatedPoiList =
      selectedReport?.pointOfInterests
        ?.filter((poi) => !selectedChaseItems.includes(poi.id))
        .map((poi) => poi.id) || [];

    updateListOfChaseItems({
      variables: {
        input: {
          reportId: selectedReport.id,
          pointOfInterestsIds: updatedPoiList,
        },
      },
    })
      .then(() => {
        setSelectedReport((previous) => {
          if (!previous) return previous;

          return {
            ...previous,
            pointOfInterests: previous.pointOfInterests?.filter(
              (poi: { id: string }) => !selectedChaseItems.includes(poi.id)
            ),
          };
        });

        setSnackbarMessage({
          shouldShow: true,
          content: <Alert severity="success">Chase items removed successfully</Alert>,
        });
        onClose();
      })
      .catch(() => {
        console.error('Failed to update chase items');
        setSnackbarMessage({
          shouldShow: true,
          content: <Alert severity="error">Failed to update chase items</Alert>,
        });
      });
  }, [
    onClose,
    selectedChaseItems,
    selectedReport,
    setSelectedReport,
    setSnackbarMessage,
    updateListOfChaseItems,
  ]);

  return (
    <>
      <GenericModal
        open={open}
        handleClose={handleClose}
        title="Removing chase items."
        crossIconStyle={{ display: 'none' }}
        titleSx={{ fontSize: 16, fontWeight: 500, py: 0 }}
        sx={{ ...removeChaseItemsDialog, height: 'fit-content' }}
      >
        <DialogContent sx={{ py: 0 }}>
          <Typography fontSize={12}>Are you sure you want to remove these chase items?</Typography>
        </DialogContent>
        <DialogActions sx={{ px: 3, py: 2 }}>
          <Button onClick={handleClose} variant="outlined" sx={{ mr: 1, fontWeight: 400 }}>
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={onUpdateListOfChaseItems}
            disabled={loading}
            disableElevation
            data-testid="remove-chase-items-button"
            sx={{
              backgroundColor: abyssColors.error[500],
              ':hover': { background: abyssColors.error[400] },
            }}
          >
            {loading ? 'Deleting' : 'Delete'}
          </Button>
        </DialogActions>
      </GenericModal>
    </>
  );
};
